import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import API_CONFIG from '../config/api';
import { apiGet, apiPost } from '../services/api';
import { processFlowPayment } from '../services/paymentsService';
import { getMiInscripcion } from '../services/eventosService';
import InscripcionCard from './profile/InscripcionCard';
import '../styles/profile/Profile.css';

const styles = {
  container: { maxWidth: 920, margin: '0 auto', padding: '1.5rem 1rem 3rem' },
  title: { fontSize: '1.5rem', fontWeight: 800, color: '#1a4d8c', margin: '0 0 6px' },
  subtitle: { color: '#666', margin: '0 0 18px' },
  card: { background: '#fff', border: '1px solid #e8e8e8', borderRadius: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' },
  sectionHeader: { background: '#1a4d8c', color: '#fff', padding: '12px 16px', fontWeight: 800, letterSpacing: 0.2 },
  sectionBody: { padding: 16, display: 'grid', gap: 12 },
  field: { display: 'grid', gap: 6 },
  label: { fontSize: 13, fontWeight: 700, color: '#333' },
  input: { padding: '10px 12px', borderRadius: 10, border: '1px solid #d9d9d9', fontSize: 14, width: '100%' },
  locked: { background: '#f3f4f6', color: '#444', cursor: 'not-allowed' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  radioList: { display: 'grid', gap: 10, padding: 14, border: '1px solid #eee', borderRadius: 12, background: '#fff' },
  radioItem: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 },
  actions: { display: 'flex', gap: 12, flexWrap: 'wrap', padding: 16, borderTop: '1px solid #eee' },
  btnPrimary: { background: '#1a4d8c', color: '#fff', border: '1px solid #1a4d8c', padding: '10px 16px', borderRadius: 10, fontWeight: 800, cursor: 'pointer' },
  btnGhost: { background: '#f4f6f8', color: '#111', border: '1px solid #ddd', padding: '10px 16px', borderRadius: 10, fontWeight: 800, cursor: 'pointer' },
  btnDanger: { background: '#fff', color: '#c62828', border: '1px solid #f3b5b5', padding: '10px 16px', borderRadius: 10, fontWeight: 800, cursor: 'pointer' },
  error: { color: '#c62828', fontSize: 14, marginTop: 10 },
  hint: { color: '#666', fontSize: 13 },
  totalBox: { padding: 14, borderRadius: 12, border: '1px solid #e9ecef', background: '#f8f9fa', display: 'grid', gap: 8 },
  totalRow: { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14 },
  totalStrong: { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 15, fontWeight: 900 },
};

const PESO_MASC = ['-52kg', '-56kg', '-60kg', '-67.5kg', '-75kg', '-82.5kg', '-90kg', '-100kg', '-110kg', '-125kg', '-140kg', '+140kg'];
const PESO_FEM = ['-44kg', '-48kg', '-52kg', '-56kg', '-60kg', '-67.5kg', '-75kg', '-82.5kg', '-90kg', '+90kg'];

const EDAD_OPCIONES = [
  { value: 'open', label: 'Open' },
  { value: 'sub_junior', label: 'Sub Junior (14-19 años)' },
  { value: 'junior', label: 'Junior (20-23 años)' },
  { value: 'master', label: 'Master (40+ años)' },
];

// ===== Reglas de negocio (iguales a las del backend, services/reglas_inscripcion.py) =====
const EDAD_MINIMA = 14;
const ORDEN_DIVISIONES = ['sub_junior', 'junior', 'open', 'master'];
const NOMBRE_DIVISION = { sub_junior: 'Sub Junior', junior: 'Junior', open: 'Open', master: 'Master' };

/** 'M'/'Masculino' → 'Masculino', 'F'/'Femenino' → 'Femenino'; cualquier otro valor → ''. */
const generoPerfil = (valor) => {
  const v = String(valor || '').trim().toLowerCase();
  if (['m', 'masculino', 'male', 'hombre'].includes(v)) return 'Masculino';
  if (['f', 'femenino', 'female', 'mujer'].includes(v)) return 'Femenino';
  return '';
};

/** Años cumplidos a la fecha de inicio del evento (fecha exacta). */
const edadALaFecha = (nacimiento, referencia) => {
  const n = String(nacimiento || '').slice(0, 10).split('-').map(Number);
  const r = String(referencia || '').slice(0, 10).split('-').map(Number);
  if (n.length !== 3 || r.length !== 3 || n.some(Number.isNaN) || r.some(Number.isNaN)) return null;
  return r[0] - n[0] - ((r[1] < n[1] || (r[1] === n[1] && r[2] < n[2])) ? 1 : 0);
};

const divisionPorEdad = (edad) => {
  if (edad === null || edad === undefined || edad < EDAD_MINIMA) return null;
  if (edad <= 19) return 'sub_junior';
  if (edad <= 23) return 'junior';
  if (edad >= 40) return 'master';
  return 'open';
};

/** Su división por edad u Open, filtradas por las categorías que incluye el evento. */
const divisionesPermitidas = (edad, categoriasEvento) => {
  const propia = divisionPorEdad(edad);
  if (!propia) return [];
  const posibles = new Set([propia, 'open']);
  const incluidas = new Set((categoriasEvento || []).map((c) => String(c || '').trim().toLowerCase().replace(/[- ]/g, '_').replace('subjunior', 'sub_junior')).filter(Boolean));
  return ORDEN_DIVISIONES.filter((d) => posibles.has(d) && (incluidas.size === 0 || incluidas.has(d)));
};

const formatoFechaCorta = (iso) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-');
  return y && m && d ? `${d}-${m}-${y}` : '';
};

const EventInternalFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const eventoId = useMemo(() => String(id || ''), [id]);

  const [loading, setLoading] = useState(true);
  const [evento, setEvento] = useState(null);
  const [me, setMe] = useState(null);
  const [afiliacionActiva, setAfiliacionActiva] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPagoDetalle, setShowPagoDetalle] = useState(false);

  const [form, setForm] = useState({
    numero_afiliacion: '',
    nombre: '',
    apellido: '',
    fecha_nacimiento: '',
    telefono: '',
    email: '',
    club_declara: '',
    genero: '',
    division: '', // tested | untested
    modalidad: '',
    modalidad_extra: [], // [] = ninguna, puede contener 'bench' y/o 'deadlift'
    division_edad: '',
    division_edad_extra: 'no', // una sola: "open"|"sub_junior"|"junior"|"master"|"no"
    categoria_peso: '', // depende genero
  });

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  // Inscripción ya existente en este evento: no se vuelve a mostrar el formulario
  const [miInscripcion, setMiInscripcion] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        // Evento desde lista pública (incluye flags y costos)
        const base = API_CONFIG.BASE_URL || '';
        const resEventos = await fetch(`${base}/api/eventos/lista?ts=${Date.now()}`, { cache: 'no-store' });
        const list = await resEventos.json().catch(() => []);
        const found = (Array.isArray(list) ? list : []).find(e => String(e.id) === String(eventoId));
        if (!found) throw new Error('No se encontró el evento.');
        setEvento(found);
        // Safety: si el evento usa formulario externo, redirigir a la URL externa
        const tipoForm = String(found?.formulario_inscripcion_tipo || '').trim().toLowerCase();
        const rawUrl = String(found?.formulario_inscripcion_url || '').trim();
        if (tipoForm === 'externo' && rawUrl) {
          const finalUrl = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
          window.location.href = finalUrl;
          return;
        }

        // Usuario actual (autocompletar)
        const resMe = await apiGet(API_CONFIG.ENDPOINTS.AUTH.ME);
        const dataMe = await resMe.json();
        if (dataMe?.success && dataMe?.data) {
          setMe(dataMe.data);
          setField('nombre', dataMe.data.nombre || '');
          setField('apellido', dataMe.data.apellido || '');
          setField('email', dataMe.data.email || '');
          setField('telefono', dataMe.data.telefono || '');
          if (dataMe.data.fecha_nacimiento) {
            const s = String(dataMe.data.fecha_nacimiento).slice(0, 10);
            setField('fecha_nacimiento', s);
          }
          setField('genero', generoPerfil(dataMe.data.genero));
        }

        // ¿Ya está inscrito? Entonces se muestra su inscripción en vez del formulario
        try {
          setMiInscripcion(await getMiInscripcion(found.id));
        } catch {
          setMiInscripcion(null);
        }

        // Afiliación activa (obligatoria para continuar)
        try {
          const resAfi = await apiGet(API_CONFIG.ENDPOINTS.AFILIACION.ME);
          const dataAfi = await resAfi.json().catch(() => ({}));
          const af = dataAfi?.afiliacion_actual || dataAfi?.afiliacion || dataAfi?.data || dataAfi;
          const activa = (af && (af.estado || '').toLowerCase() === 'activa') ? af : null;
          setAfiliacionActiva(activa);
          if (activa?.numero_afiliacion) {
            setField('numero_afiliacion', String(activa.numero_afiliacion));
          }
        } catch {
          setAfiliacionActiva(null);
        }
      } catch (e) {
        setError(e.message || 'Error cargando el formulario.');
      } finally {
        setLoading(false);
      }
    };
    if (eventoId) load();
  }, [eventoId]);

  const modalidadesDisponibles = useMemo(() => {
    const mod = evento?.modalidad;
    return Array.isArray(mod) ? mod : [];
  }, [evento]);

  const modalidadExtraOpciones = useMemo(() => {
    const set = new Set((modalidadesDisponibles || []).map(m => String(m || '').trim().toLowerCase()).filter(Boolean));
    const opts = [];
    if (set.has('bench')) opts.push({ value: 'bench', label: 'Only Bench (sólo Banca)' });
    if (set.has('deadlift')) opts.push({ value: 'deadlift', label: 'Only Deadlift (sólo Peso Muerto)' });
    return opts;
  }, [modalidadesDisponibles]);

  const labelModalidad = (m) => {
    const labels = { raw: 'RAW (Rodilleras)', classic_raw: 'Classic Raw (Vendas)', single_ply: 'Single Ply', bench: 'Only Bench (sólo Banca)', deadlift: 'Only Deadlift (sólo Peso Muerto)', equipado: 'Equipado' };
    return labels[m] || String(m || '').toUpperCase();
  };

  const formatCLP = (m) => `$${Number(m || 0).toLocaleString('es-CL')}`;

  // Edad a la fecha de inicio del evento y divisiones de edad que puede elegir
  const edadEvento = useMemo(
    () => edadALaFecha(form.fecha_nacimiento, evento?.fecha_inicio),
    [form.fecha_nacimiento, evento?.fecha_inicio]
  );
  const permitidas = useMemo(
    () => divisionesPermitidas(edadEvento, evento?.categorias_incluidas),
    [edadEvento, evento?.categorias_incluidas]
  );
  const extrasEdadPermitidos = (form.division_edad && form.division_edad !== 'open' && permitidas.includes('open')) ? ['open'] : [];

  // División por defecto: la propia por edad (si el evento la incluye) o la primera permitida
  useEffect(() => {
    if (!permitidas.length) return;
    if (!permitidas.includes(form.division_edad)) {
      const propia = divisionPorEdad(edadEvento);
      setField('division_edad', permitidas.includes(propia) ? propia : permitidas[0]);
      setField('division_edad_extra', 'no');
      setShowPagoDetalle(false);
    }
  }, [permitidas]);

  // La modalidad extra no puede repetir la principal
  useEffect(() => {
    if (form.modalidad && form.modalidad_extra.includes(form.modalidad)) {
      setField('modalidad_extra', form.modalidad_extra.filter((v) => v !== form.modalidad));
      setShowPagoDetalle(false);
    }
  }, [form.modalidad]);

  // Si cambian las modalidades permitidas, asegurar que modalidad_extra sea válida
  useEffect(() => {
    if (!evento?.modalidad_extra_habilitada) {
      if (form.modalidad_extra.length > 0) {
        setField('modalidad_extra', []);
        setShowPagoDetalle(false);
      }
      return;
    }
    const allowed = new Set((modalidadExtraOpciones || []).map(o => o.value));
    const filtered = form.modalidad_extra.filter(v => allowed.has(v));
    if (filtered.length !== form.modalidad_extra.length) {
      setField('modalidad_extra', filtered);
      setShowPagoDetalle(false);
    }
  }, [evento?.modalidad_extra_habilitada, modalidadExtraOpciones]);

  const getBaseInscripcion = () => {
    const det = (evento?.costos_inscripcion_detalle && typeof evento.costos_inscripcion_detalle === 'object')
      ? evento.costos_inscripcion_detalle
      : {};
    const hasClub = !!me?.club_nombre;
    const key = form.division === 'tested'
      ? (hasClub ? 'tested_con_club' : 'tested_independiente')
      : (hasClub ? 'untested_con_club' : 'untested_independiente');
    const base = Number(det[key] || 0);
    return Number.isFinite(base) ? base : 0;
  };

  const pagoDetalle = useMemo(() => {
    if (!evento) return null;
    const base = getBaseInscripcion();
    const extraModalidad = (evento.modalidad_extra_habilitada && form.modalidad_extra.length > 0)
      ? Number(evento.modalidad_extra_monto || 0) * form.modalidad_extra.length
      : 0;
    // Montos iguales: modalidad_extra_monto y edad_extra_monto. Se cobran por separado.
    const extraEdad = (evento.edad_extra_habilitada && form.division_edad_extra && form.division_edad_extra !== 'no')
      ? Number(evento.modalidad_extra_monto || 0)
      : 0;
    const extras = [
      ...(base > 0 ? [{ label: 'Inscripción', amount: base }] : []),
      ...(extraModalidad > 0 ? [{ label: 'Modalidad extra', amount: extraModalidad }] : []),
      ...(extraEdad > 0 ? [{ label: 'División de edad extra', amount: extraEdad }] : []),
    ];
    const total = extras.reduce((acc, it) => acc + Number(it.amount || 0), 0);
    return { items: extras, total };
  }, [evento, me, form.division, form.modalidad_extra, form.division_edad_extra]);

  useEffect(() => {
    if (form.division_edad_extra !== 'no' && !extrasEdadPermitidos.includes(form.division_edad_extra)) {
      setField('division_edad_extra', 'no');
      setShowPagoDetalle(false);
    }
  }, [form.division_edad, permitidas]);

  const validateBeforePago = () => {
    if (!evento) return 'No se encontró el evento.';
    if (evento.formulario_inscripcion_tipo && evento.formulario_inscripcion_tipo !== 'interno') return 'Este evento no usa formulario interno.';
    if (!afiliacionActiva?.numero_afiliacion) return 'No puedes continuar con la inscripción, porque no estás afiliado a Achipo';
    if (!form.division) return 'Selecciona una división (TESTED o UNTESTED).';
    if (!form.genero) return 'Selecciona género.';
    if (!form.categoria_peso) return 'Selecciona categoría de peso.';
    if (!form.modalidad) return 'Selecciona modalidad.';
    if (!form.division_edad) return 'Selecciona división de edad.';
    if (!permitidas.includes(form.division_edad)) return 'La división de edad no corresponde a tu edad a la fecha del evento.';
    if (form.modalidad_extra.includes(form.modalidad)) return 'La modalidad extra no puede ser la misma que tu modalidad principal.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const v = validateBeforePago();
    if (v) {
      setError(v);
      return;
    }

    // Paso 1: mostrar detalle antes de ir a Flow
    if (!showPagoDetalle) {
      setShowPagoDetalle(true);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        // El backend toma el número desde la BD; se envía solo para mostrarlo/compatibilidad
        numero_afiliacion: form.numero_afiliacion,
        nombre: form.nombre,
        apellido: form.apellido,
        fecha_nacimiento: form.fecha_nacimiento,
        telefono: form.telefono,
        email: form.email,
        club_declara: me?.club_nombre || null,
        genero: form.genero,
        division: form.division,
        modalidad: form.modalidad,
        modalidad_extra: form.modalidad_extra.length > 0 ? form.modalidad_extra.join(',') : 'no',
        division_edad: form.division_edad,
        division_edad_extra: form.division_edad_extra,
        categoria_edad: form.division_edad,
        categoria_peso_tested: form.division === 'tested' ? form.categoria_peso : null,
        categoria_peso_untested: form.division === 'untested' ? form.categoria_peso : null,
      };

      const resIns = await apiPost(API_CONFIG.ENDPOINTS.EVENTOS.INSCRIBIRSE(evento.id), payload);
      const dataIns = await resIns.json().catch(() => ({}));
      if (!resIns.ok || !dataIns.ok) {
        throw new Error(dataIns.error || 'No se pudo crear la inscripción.');
      }

      const { payment_url } = await processFlowPayment({
        orderId: dataIns.order_id,
        amount: dataIns.amount,
        email: form.email || me?.email,
        subject: `Inscripción evento: ${evento.nombre || evento.id}`,
      });

      window.location.href = payment_url;
    } catch (err) {
      // Si el backend avisa que ya está inscrito, se muestra su inscripción
      try {
        const existente = await getMiInscripcion(evento.id);
        if (existente) {
          setMiInscripcion(existente);
          return;
        }
      } catch { /* se muestra el error original */ }
      setError(err.message || 'No se pudo enviar la inscripción.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div style={styles.container}>
          <p>Cargando formulario…</p>
        </div>
        <Footer />
      </>
    );
  }

  if (!evento) {
    return (
      <>
        <Header />
        <div style={styles.container}>
          <p>No se encontró el evento.</p>
          {error ? <p style={styles.error}>{error}</p> : null}
          <button type="button" style={styles.btnGhost} onClick={() => navigate('/events')}>Volver a eventos</button>
        </div>
        <Footer />
      </>
    );
  }

  if (miInscripcion) {
    return (
      <>
        <Header />
        <div style={styles.container}>
          <h1 style={styles.title}>Ya estás inscrito</h1>
          <p style={styles.subtitle}>
            {evento.nombre}. No es necesario volver a llenar el formulario.
            {miInscripcion.pago_pendiente ? ' Solo falta completar el pago.' : ''}
          </p>
          <InscripcionCard inscripcion={miInscripcion} mostrarEvento={false} />
          <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" style={styles.btnGhost} onClick={() => navigate('/profile/mis-eventos')}>
              Ver mis eventos
            </button>
            <button type="button" style={styles.btnGhost} onClick={() => navigate('/events')}>
              Volver a eventos
            </button>
          </div>
          <p style={{ marginTop: 14, color: '#777', fontSize: 13 }}>
            Si necesitas cambiar algún dato de tu inscripción, escríbele a la organización.
          </p>
        </div>
        <Footer />
      </>
    );
  }

  if (evento?.formulario_inscripcion_tipo === 'interno' && !afiliacionActiva?.numero_afiliacion) {
    return (
      <>
        <Header />
        <div style={styles.container}>
          <h1 style={styles.title}>Formulario de inscripción</h1>
          <p style={styles.subtitle}>{evento.nombre}</p>
          <div style={styles.card}>
            <div style={styles.sectionBody}>
              <p style={{ margin: 0, fontWeight: 700, color: '#c62828' }}>
                No puedes continuar con la inscripción, porque no estás afiliado a Achipo
              </p>
              <div style={{ marginTop: 12 }}>
                <button type="button" style={styles.btnGhost} onClick={() => navigate('/profile')}>
                  Ir a mi perfil
                </button>
                <button type="button" style={{ ...styles.btnGhost, marginLeft: 10 }} onClick={() => navigate('/events')}>
                  Volver a eventos
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const perfilIncompleto = !form.fecha_nacimiento || !form.genero;
  if (perfilIncompleto || !permitidas.length) {
    const mensaje = perfilIncompleto
      ? 'Completa tu perfil: falta tu fecha de nacimiento o tu género. Contacta a ACHIPO.'
      : (edadEvento !== null && edadEvento < EDAD_MINIMA)
        ? `Debes tener al menos ${EDAD_MINIMA} años a la fecha del evento (${formatoFechaCorta(evento.fecha_inicio)}) para inscribirte.`
        : 'Este evento no incluye una división de edad disponible para ti.';
    return (
      <>
        <Header />
        <div style={styles.container}>
          <h1 style={styles.title}>Formulario de inscripción</h1>
          <p style={styles.subtitle}>{evento.nombre}</p>
          <div style={styles.card}>
            <div style={styles.sectionBody}>
              <p style={{ margin: 0, fontWeight: 700, color: '#c62828' }}>{mensaje}</p>
              <div style={{ marginTop: 12 }}>
                <button type="button" style={styles.btnGhost} onClick={() => navigate('/profile')}>Ir a mi perfil</button>
                <button type="button" style={{ ...styles.btnGhost, marginLeft: 10 }} onClick={() => navigate('/events')}>Volver a eventos</button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const showTested = !!evento.incluye_tested;
  const showUntested = !!evento.incluye_untested;
  const pesoOpciones = (String(form.genero || '').toLowerCase() === 'femenino') ? PESO_FEM : PESO_MASC;

  return (
    <>
      <Header />
      <div style={styles.container}>
        <h1 style={{ ...styles.title, marginTop: '3cm' }}>Formulario de inscripción</h1>
        <p style={styles.subtitle}>{evento.nombre}</p>

        <form onSubmit={handleSubmit}>
          <div style={styles.card}>
            <div style={styles.sectionHeader}>DATOS DE COMPETENCIA</div>
            <div style={styles.sectionBody}>
              <div style={styles.field}>
                <div style={styles.label}>DIVISIÓN *</div>
                <div style={styles.radioList}>
                  {showTested && (
                    <label style={styles.radioItem}>
                      <input type="radio" name="division" checked={form.division === 'tested'} onChange={() => setField('division', 'tested')} />
                      TESTED
                    </label>
                  )}
                  {showUntested && (
                    <label style={styles.radioItem}>
                      <input type="radio" name="division" checked={form.division === 'untested'} onChange={() => setField('division', 'untested')} />
                      OPEN (NO TESTEADO)
                    </label>
                  )}
                  {!showTested && !showUntested && (
                    <div style={styles.hint}>Este evento no tiene división configurada.</div>
                  )}
                </div>
              </div>

              <div style={styles.field}>
                <div style={styles.label}>Modalidad *</div>
                <div style={styles.radioList}>
                  {modalidadesDisponibles.map((m) => (
                    <label key={m} style={styles.radioItem}>
                      <input type="radio" name="modalidad" checked={form.modalidad === m} onChange={() => setField('modalidad', m)} />
                      {labelModalidad(m)}
                    </label>
                  ))}
                </div>
              </div>

              <div style={styles.field}>
                <div style={styles.label}>Modalidad Extra *</div>
                <div style={styles.radioList}>
                  {!evento.modalidad_extra_habilitada ? (
                    <div style={styles.hint}>Este evento no permite modalidad extra.</div>
                  ) : modalidadExtraOpciones.length === 0 ? (
                    <div style={styles.hint}>Este evento no tiene opciones disponibles para modalidad extra.</div>
                  ) : (
                    <>
                  {modalidadExtraOpciones.map((o) => (
                    <label key={o.value} style={{ ...styles.radioItem, ...(o.value === form.modalidad ? { opacity: 0.45, cursor: 'not-allowed' } : null) }} title={o.value === form.modalidad ? 'Ya es tu modalidad principal' : undefined}>
                      <input
                        type="checkbox"
                        disabled={o.value === form.modalidad}
                        checked={form.modalidad_extra.includes(o.value)}
                        onChange={() => {
                          const next = form.modalidad_extra.includes(o.value)
                            ? form.modalidad_extra.filter(v => v !== o.value)
                            : [...form.modalidad_extra, o.value];
                          setField('modalidad_extra', next);
                          setShowPagoDetalle(false);
                        }}
                      />
                      {o.label}
                    </label>
                  ))}
                  <label style={styles.radioItem}>
                    <input
                      type="checkbox"
                      checked={form.modalidad_extra.length === 0}
                      onChange={() => { setField('modalidad_extra', []); setShowPagoDetalle(false); }}
                    />
                    NO
                  </label>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div style={{ height: 14 }} />

          <div style={styles.card}>
            <div style={styles.sectionHeader}>DATOS DE AFILIACIÓN</div>
            <div style={styles.sectionBody}>
              <div style={styles.hint}>
                Nombre, apellido, fecha de nacimiento y género se toman de tu perfil. Si hay un error, contacta a ACHIPO.
              </div>
              <div style={styles.field}>
                <label style={styles.label}>NÚMERO DE AFILIACIÓN *</label>
                <div style={styles.hint}>Lo encontrarás en tu perfil de la web www.Achipo.cl</div>
                <input style={styles.input} value={form.numero_afiliacion} readOnly />
              </div>

              <div style={styles.row2}>
                <div style={styles.field}>
                  <label style={styles.label}>Nombre *</label>
                  <input style={{ ...styles.input, ...styles.locked }} value={form.nombre} readOnly title="Se toma de tu perfil" />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Apellido *</label>
                  <input style={{ ...styles.input, ...styles.locked }} value={form.apellido} readOnly title="Se toma de tu perfil" />
                </div>
              </div>

              <div style={styles.row2}>
                <div style={styles.field}>
                  <label style={styles.label}>Fecha de Nacimiento *</label>
                  <input style={{ ...styles.input, ...styles.locked }} value={formatoFechaCorta(form.fecha_nacimiento)} readOnly title="Se toma de tu perfil" />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Número de Teléfono *</label>
                  <input style={styles.input} value={form.telefono} onChange={e => setField('telefono', e.target.value)} />
                </div>
              </div>

              <div style={styles.row2}>
                <div style={styles.field}>
                  <label style={styles.label}>e-mail *</label>
                  <input type="email" style={styles.input} value={form.email} onChange={e => setField('email', e.target.value)} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>¿Estás afiliado a un Club?</label>
                  <input
                    style={styles.input}
                    value={me?.club_nombre ? me.club_nombre : 'Sin club'}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ height: 14 }} />

          <div style={styles.card}>
            <div style={styles.sectionHeader}>CATEGORÍAS</div>
            <div style={styles.sectionBody}>
              <div style={styles.field}>
                <div style={styles.label}>Género *</div>
                <input style={{ ...styles.input, ...styles.locked, maxWidth: 260 }} value={form.genero} readOnly title="Se toma de tu perfil" />
              </div>

              <div style={styles.field}>
                <div style={styles.label}>Categoría de Peso *</div>
                <div style={styles.radioList}>
                  {pesoOpciones.map((p) => (
                    <label key={p} style={styles.radioItem}>
                      <input type="radio" name="categoria_peso" checked={form.categoria_peso === p} onChange={() => setField('categoria_peso', p)} />
                      {p}
                    </label>
                  ))}
                </div>
              </div>

              <div style={styles.field}>
                <div style={styles.label}>División de Edad *</div>
                <div style={styles.radioList}>
                  {EDAD_OPCIONES.map((o) => {
                    const ok = permitidas.includes(o.value);
                    return (
                      <label key={o.value} style={{ ...styles.radioItem, ...(ok ? null : { opacity: 0.45, cursor: 'not-allowed' }) }}>
                        <input type="radio" name="division_edad" disabled={!ok} checked={form.division_edad === o.value} onChange={() => { setField('division_edad', o.value); setField('division_edad_extra', 'no'); setShowPagoDetalle(false); }} />
                        {o.label}
                      </label>
                    );
                  })}
                  <div style={styles.hint}>
                    Con {edadEvento} años al {formatoFechaCorta(evento.fecha_inicio)} (inicio del evento) puedes elegir: {permitidas.map((d) => NOMBRE_DIVISION[d]).join(' u ')}.
                  </div>
                </div>
              </div>

              <div style={styles.field}>
                <div style={styles.label}>División de Edad Extra *</div>
                <div style={styles.radioList}>
                  {!evento.edad_extra_habilitada ? (
                    <div style={styles.hint}>Este evento no permite división de edad extra.</div>
                  ) : (
                    <>
                      {EDAD_OPCIONES
                        .filter(o => extrasEdadPermitidos.includes(o.value))
                        .map((o) => (
                          <label key={o.value} style={styles.radioItem}>
                            <input
                              type="radio"
                              name="division_edad_extra"
                              checked={form.division_edad_extra === o.value}
                              onChange={() => { setField('division_edad_extra', o.value); setShowPagoDetalle(false); }}
                            />
                            {o.label}
                          </label>
                        ))}
                      <label style={styles.radioItem}>
                        <input
                          type="radio"
                          name="division_edad_extra"
                          checked={form.division_edad_extra === 'no'}
                          onChange={() => { setField('division_edad_extra', 'no'); setShowPagoDetalle(false); }}
                        />
                        NO
                      </label>
                      <div style={styles.hint}>
                        {extrasEdadPermitidos.length
                          ? <>Puedes sumar Open como división extra. Costo fijo: {formatCLP(evento.modalidad_extra_monto || 0)}</>
                          : 'Solo se puede sumar Open como extra cuando tu división de edad no es Open.'}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {showPagoDetalle && pagoDetalle && (
                <div style={styles.totalBox}>
                  <div style={{ fontWeight: 900, color: '#1a4d8c' }}>Detalle del pago</div>
                  {pagoDetalle.items.length === 0 ? (
                    <div style={styles.hint}>Este evento no tiene costo configurado (Gratis).</div>
                  ) : (
                    pagoDetalle.items.map((it, idx) => (
                      <div key={idx} style={styles.totalRow}>
                        <span>{it.label}</span>
                        <span>{formatCLP(it.amount)}</span>
                      </div>
                    ))
                  )}
                  <div style={{ height: 1, background: '#e9ecef' }} />
                  <div style={styles.totalStrong}>
                    <span>Total</span>
                    <span>{formatCLP(pagoDetalle.total)}</span>
                  </div>
                  <div style={styles.hint}>
                    El total final se confirmará al crear la inscripción.
                  </div>
                </div>
              )}
            </div>
            <div style={styles.actions}>
              <button type="button" style={styles.btnGhost} onClick={() => navigate('/events')} disabled={saving}>Volver</button>
              {showPagoDetalle && (
                <button
                  type="button"
                  style={styles.btnDanger}
                  onClick={() => setShowPagoDetalle(false)}
                  disabled={saving}
                >
                  Editar selección
                </button>
              )}
              <button type="submit" style={styles.btnPrimary} disabled={saving}>
                {saving ? 'Enviando…' : (showPagoDetalle ? 'Proceder a pago' : 'Ver detalle de pago')}
              </button>
            </div>
          </div>

          {error ? <div style={styles.error}>{error}</div> : null}
        </form>
      </div>
      <Footer />
    </>
  );
};

export default EventInternalFormPage;